import { supabase } from "@/integrations/supabase/client";

export interface Testimonial {
  id: string;
  quote: string;
  rating: number;
  company?: string;
  city?: string;
  service?: string;
  name?: string;
  initials?: string;
  logo?: string;
}

// Placeholder testimonials per aziende (vendors)
const PLACEHOLDER_VENDOR_TESTIMONIALS: Testimonial[] = [
  {
    id: "vendor-1",
    quote: "Da quando siamo su Halomora abbiamo triplicato le prenotazioni. Il sistema di gestione è intuitivo e i clienti arrivano già qualificati.",
    rating: 5,
    company: "Pulizie Professionali Jesolo",
    city: "Jesolo",
    logo: undefined,
  },
  {
    id: "vendor-2",
    quote: "Ottima piattaforma per far crescere il business. Il supporto è sempre disponibile e le commissioni sono trasparenti.",
    rating: 5,
    company: "Clean Service Venezia",
    city: "Venezia",
    logo: undefined,
  },
  {
    id: "vendor-3",
    quote: "Finalmente un marketplace che funziona davvero. I clienti sono seri e i pagamenti arrivano puntuali. Consigliato!",
    rating: 5,
    company: "Mani Pulite Caorle",
    city: "Caorle",
    logo: undefined,
  },
];

// Placeholder testimonials per clienti
const PLACEHOLDER_CUSTOMER_TESTIMONIALS: Testimonial[] = [
  {
    id: "customer-1",
    quote: "Servizio eccellente! Ho prenotato una pulizia per il mio appartamento vacanze e tutto è stato perfetto. Professionalità e puntualità.",
    rating: 5,
    name: "Marco R.",
    initials: "MR",
    city: "Jesolo",
    service: "Pulizie appartamenti vacanze",
  },
  {
    id: "customer-2",
    quote: "Finalmente una piattaforma che funziona davvero. Ho trovato un'azienda di pulizie affidabile e i prezzi sono chiari fin dall'inizio.",
    rating: 5,
    name: "Sara M.",
    initials: "SM",
    city: "Venezia",
    service: "Pulizie domestiche",
  },
  {
    id: "customer-3",
    quote: "Ottima esperienza! L'azienda è arrivata puntuale, ha fatto un lavoro perfetto e il prezzo era quello indicato. Tornerò sicuramente.",
    rating: 5,
    name: "Luca B.",
    initials: "LB",
    city: "Caorle",
    service: "Pulizie post-cantiere",
  },
];

/**
 * S2d-T8 · FIND-02 — PostgREST restituisce una relazione embeddata come oggetto
 * oppure come array a seconda di come inferisce la cardinalita', e l'inferenza
 * cambia con la forma della query. Fidarsi di una delle due forme e' un crash
 * rimandato: stesso accorgimento gia' adottato per `tenants(...)` in
 * `TenantProvider` (S2a-T7).
 */
function embedded<T>(value: T | T[] | null | undefined): T | undefined {
  if (Array.isArray(value)) return value[0];
  return value ?? undefined;
}

/** Forme minime delle relazioni embeddate lette qui sotto. */
type EmbeddedService = { name?: string | null };
type EmbeddedBooking = { services?: EmbeddedService | EmbeddedService[] | null };
type EmbeddedProfile = { full_name?: string | null };

/**
 * Recupera testimonianze reali dal DB per aziende (vendors)
 * Se non sufficienti, completa con placeholder
 */
export async function getVendorTestimonials(minCount: number = 3): Promise<Testimonial[]> {
  try {
    // Recupera reviews con commenti e info azienda
    const { data: reviews, error } = await supabase
      .from("reviews")
      .select(`
        id,
        rating,
        comment,
        company_id,
        created_at,
        companies!inner (
          company_name,
          city,
          logo_url
        )
      `)
      .not("comment", "is", null)
      .neq("comment", "")
      .gte("rating", 4)
      .order("created_at", { ascending: false })
      .limit(10);

    if (error) {
      console.warn("Error fetching vendor testimonials:", error);
      return PLACEHOLDER_VENDOR_TESTIMONIALS.slice(0, minCount);
    }

    const realTestimonials: Testimonial[] = (reviews || [])
      .filter((r: any) => r.comment && r.companies)
      .map((r: any) => ({
        id: r.id,
        quote: r.comment,
        rating: r.rating,
        company: r.companies?.company_name || "Azienda",
        city: r.companies?.city || "",
        logo: r.companies?.logo_url || undefined,
      }));

    // Se abbiamo abbastanza testimonianze reali, usiamo quelle
    if (realTestimonials.length >= minCount) {
      return realTestimonials.slice(0, minCount);
    }

    // Altrimenti completiamo con placeholder
    const needed = minCount - realTestimonials.length;
    return [...realTestimonials, ...PLACEHOLDER_VENDOR_TESTIMONIALS.slice(0, needed)];
  } catch (error) {
    console.warn("Error in getVendorTestimonials:", error);
    return PLACEHOLDER_VENDOR_TESTIMONIALS.slice(0, minCount);
  }
}

/**
 * Recupera testimonianze reali dal DB per clienti
 * Se non sufficienti, completa con placeholder
 */
export async function getCustomerTestimonials(minCount: number = 3): Promise<Testimonial[]> {
  try {
    // Recupera reviews con commenti e info booking (per servizio)
    const { data: reviews, error } = await supabase
      .from("reviews")
      .select(`
        id,
        rating,
        comment,
        customer_id,
        booking_id,
        created_at,
        bookings!inner (
          service_id,
          scheduled_date,
          services (
            name
          )
        ),
        profiles!reviews_customer_id_fkey (
          full_name
        )
      `)
      .not("comment", "is", null)
      .neq("comment", "")
      .gte("rating", 4)
      .order("created_at", { ascending: false })
      .limit(10);

    if (error) {
      console.warn("Error fetching customer testimonials:", error);
      return PLACEHOLDER_CUSTOMER_TESTIMONIALS.slice(0, minCount);
    }

    const realTestimonials: Testimonial[] = (reviews || [])
      .filter((r: any) => r.comment)
      .map((r: any) => {
        // S2d-T8: il servizio si legge attraverso il booking, non da `reviews`.
        const booking = embedded<EmbeddedBooking>(r.bookings);
        const service = embedded<EmbeddedService>(booking?.services);
        const profile = embedded<EmbeddedProfile>(r.profiles);
        const fullName = profile?.full_name || "";
        const nameParts = fullName.split(" ");
        const initials = nameParts.length >= 2
          ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`
          : fullName.substring(0, 2).toUpperCase();

        return {
          id: r.id,
          quote: r.comment,
          rating: r.rating,
          name: fullName || "Cliente verificato",
          initials: initials || "CV",
          service: service?.name || "Servizio",
        };
      });

    // Se abbiamo abbastanza testimonianze reali, usiamo quelle
    if (realTestimonials.length >= minCount) {
      return realTestimonials.slice(0, minCount);
    }

    // Altrimenti completiamo con placeholder
    const needed = minCount - realTestimonials.length;
    return [...realTestimonials, ...PLACEHOLDER_CUSTOMER_TESTIMONIALS.slice(0, needed)];
  } catch (error) {
    console.warn("Error in getCustomerTestimonials:", error);
    return PLACEHOLDER_CUSTOMER_TESTIMONIALS.slice(0, minCount);
  }
}
